<script setup>
import '@campact/camper/core/CmprCopyButton';
import '@campact/camper/core/CmprFormHint';
import '@campact/camper/cta/CmprPrefilledArea';
import '@campact/camper/icons/CmprIconSolidCircleInfo';

import { computed, inject } from 'vue';
import StepItem from './StepItem.vue';
import PaymentTypeChoicesGroup from './PaymentTypeChoicesGroup.vue';
import IbanInput from './IbanInput.vue';
import AdyenCreditcard from './AdyenCreditcard.vue';
import MobilePaymentTypes from './MobilePaymentTypes.vue';
import CmprInput from '@cta/frontend/components/CmprInput.vue';
import CmprCheckbox from '@cta/frontend/components/CmprCheckbox.vue';

const store = inject('store');

const props = defineProps({
  stepNumber: {
    type: Number,
    default: 3,
  },
  paymentTypes: {
    type: Array,
    default() {
      return [];
    },
  },
  prefilledAttributes: {
    type: Object,
    default() {
      return {
        paymentType: undefined,
        owner: undefined,
        iban: undefined,
      };
    },
  },
  prefillBankaccountFormCheckboxShow: {
    type: Boolean,
    default: false,
  },
  prefillBankaccountFormCheckboxText: {
    type: String,
    default: '',
  },
  donationAccountIban: {
    type: String,
    default: undefined,
  },
  submitButtonLabel: {
    type: String,
    default: 'Spenden',
  },
  banktransferLabel: {
    type: String,
    default: 'Überweisung',
  },
  analyticsIdPrefix: {
    type: String,
    default: 'donate-call',
  },
  adyenSettings: {
    type: Object,
    default() {
      return {
        clientKey: undefined,
        environment: undefined,
        gatewayMerchantId: undefined,
        googlePayMerchantId: undefined,
        merchantName: undefined,
        mobilePaymentTypes: {
          type: Array,
          default: () => [],
        },
        paymentPath: undefined,
        paymentMethodsPath: undefined,
      };
    },
  },
});

const emit = defineEmits(['try-open', 'validate', 'commit', 'submit']);

const showPrefilledPaymentArea = computed(() => store.paymentPrefilled && !store.paymentErrorUnlocked);

const showPaymentSummary = computed(() => (store.values.paymentType === 'directdebit'
  && [store.values.owner, store.values.iban].every((value) => !value))
|| store.values.paymentType);

const paymentCompleted = computed(() => {
  switch (store.values.paymentType) {
    case 'directdebit':
      return (store.paymentErrorUnlocked ? store.paymentStepValid : true)
        && !!store.values.owner
        && !!store.values.iban;
    case 'apple_pay':
    case 'creditcard_adyen':
    case 'google_pay':
    case 'klarna_paynow':
    case 'paybybank':
      return !!store.values.adyenData;
    default:
      return !!store.values.paymentType;
  }
});
const paymentStepTitle = computed(() => {
  if (paymentCompleted.value) return 'Zahlungsart ausgewählt';
  if (!store.values.paymentType) return 'Zahlungsart wählen';
  return 'Zahlungsart vervollständigen';
});
const paymentTypeLabels = {
  banktransfer: props.banktransferLabel,
  creditcard_adyen: 'Kreditkarte oder Debitkarte',
  directdebit: 'Lastschrift',
  klarna_paynow: 'Klarna Pay Now',
  paybybank: 'Pay by Bank (Tink)',
  paypal: 'PayPal',
};
const paymentSummaryTitle = computed(() => {
  if (store.values.adyenData && store.values.paymentType === 'google_pay') {
    return 'Google Pay';
  } else if (store.values.adyenData && store.values.paymentType === 'apple_pay') {
    return 'Apple Pay';
  } else {
    return paymentTypeLabels[store.values.paymentType];
  }
});
const paymentSummaryBody = computed(() => {
  if (store.values.paymentType !== 'directdebit') return undefined;
  return [store.values.owner, store.values.iban ? `IBAN ${store.values.iban}` : undefined]
    .filter((value) => !!value).join(' · ');
});
const prefilledPaymentSummaryBody = computed(() => [
  props.prefilledAttributes.owner ? store.values.owner : undefined,
  props.prefilledAttributes.iban ? `IBAN ${store.values.iban}` : undefined,
].filter((value) => !!value).join(' · '));

const paymentTypeChoices = computed(() => {
  return props
    .paymentTypes
    .filter((type) => type !== 'mobile_payment_types')
    .map((type) => { return { paymentType: type, label: paymentTypeLabels[type] }; });
});

function editPrefilledPayment() {
  store.paymentPrefilled = false;
  store.values.owner = '';
  store.values.iban = '';
}

function validatePaymentIfNeeded(attribute) {
  if (store.paymentErrorUnlocked && store.errors[attribute]) emit('validate');
}
</script>

<template>
  <div class="payment-step">
    <StepItem
      :number="stepNumber"
      :opened="store.paymentStepOpen"
      :title="paymentStepTitle"
      @try-open="emit('try-open')"
    >
      <template #opened>
        <div class="payment-step__body">
          <MobilePaymentTypes
            v-if="paymentTypes.includes('mobile_payment_types')"
            :adyen-settings="adyenSettings"
            @submit="emit('submit')"
          />

          <PaymentTypeChoicesGroup
            v-model:choice="store.values.paymentType"
            :analytics-id-prefix="analyticsIdPrefix"
            :choices="paymentTypeChoices"
            @update:choice="store.errors.paymentType = undefined"
          >
            <template #directdebit>
              <div v-if="!showPrefilledPaymentArea">
                <CmprInput
                  v-model="store.values.owner"
                  class="payment-step__owner"
                  :class="{'is-invalid': store.paymentErrorUnlocked && store.errors.owner}"
                  data-analytics-context="payment-step"
                  :data-analytics-id="`${analyticsIdPrefix}-form-payment-type-directdebit-owner-field`"
                  data-analytics-inner-text="owner"
                  placeholder="Kontoinhaber*in"
                  @input="validatePaymentIfNeeded('owner')"
                  @keydown.enter="emit('commit')"
                />

                <cmpr-form-hint
                  v-if="store.paymentErrorUnlocked && store.errors.owner"
                  variant="error"
                >
                  {{ store.errors.owner }}
                </cmpr-form-hint>
              </div>

              <div v-if="!showPrefilledPaymentArea">
                <IbanInput
                  v-model:iban="store.values.iban"
                  v-model:raw-iban="store.values.rawIban"
                  class="payment-step__iban"
                  :class="{'is-invalid': store.paymentErrorUnlocked && store.errors.iban}"
                  data-analytics-context="payment-step"
                  :data-analytics-id="`${analyticsIdPrefix}-form-payment-type-directdebit-iban-field`"
                  data-analytics-inner-text="iban"
                  @input="validatePaymentIfNeeded('iban')"
                  @keydown.enter="emit('commit')"
                />

                <cmpr-form-hint
                  v-if="store.paymentErrorUnlocked && store.errors.iban"
                  variant="error"
                >
                  {{ store.errors.iban }}
                </cmpr-form-hint>

                <div
                  v-if="prefillBankaccountFormCheckboxShow"
                  class="payment-step__prefill-bankaccount"
                >
                  <div
                    class="cmpr-checkbox-control"
                    data-size="medium"
                  >
                    <CmprCheckbox
                      id="prefill-bankaccount"
                      v-model="store.values.allowPrefillBankAccount"
                      class="payment-step__prefill-bankaccount__input"
                      data-analytics-context="payment-step"
                      :data-analytics-id="`${analyticsIdPrefix}-form-payment-type-directdebit-prefill-bankaccount-checkbox`"
                      data-analytics-inner-text="prefill-bankaccount"
                      name="prefill-bankaccount"
                    />

                    <label
                      class="cmpr-checkbox-control__label payment-step__prefill-bankaccount__label"
                      for="prefill-bankaccount"
                    >
                      Bankverbindung merken
                    </label>

                    <div
                      class="payment-step__prefill-bankaccount__description"
                    >
                      {{ prefillBankaccountFormCheckboxText }}
                    </div>
                  </div>
                </div>
              </div>

              <cmpr-prefilled-area
                v-if="showPrefilledPaymentArea"
                class="payment-step__prefilled-attributes-summary"
                editable="editable"
                @click.prevent="editPrefilledPayment"
              >
                <span
                  v-if="prefilledPaymentSummaryBody"
                >
                  {{ prefilledPaymentSummaryBody }}
                </span>
              </cmpr-prefilled-area>

              <div
                class="payment-step__notice"
              >
                <cmpr-icon-solid-circle-info class="payment-step__notice-icon" />
                Ich ermächtige Campact e.V., den oben genannten Betrag von meinem Konto durch Lastschrift einzuziehen.
              </div>
            </template>

            <template #banktransfer>
              <div class="payment-step__banktransfer-info">
                <slot name="banktransfer-advice">
                  <p>
                    <strong>Spendenkonto</strong><br>
                    Campact e.V.<br>
                    IBAN: {{ donationAccountIban }}<br>
                    BIC: BFSWDE33HAN<br>
                    Bank: SozialBank
                  </p>
                  <p>
                    Bitte schließe Deine Eingabe mit „{{ submitButtonLabel }}“ ab.
                    Du erhältst umgehend eine E-Mail für Deine Unterlagen.
                  </p>
                  <p>
                    <strong>Wichtiger Hinweis</strong>: Achte bitte darauf, dass der <strong>Empfängername</strong> exakt
                    „<strong>Campact e.V.</strong>“ lautet.
                  </p>
                  <p class="m-0">
                    Viele Banken prüfen inzwischen per SEPA-Namensabgleich („Verification of Payee“), ob der angegebene
                    Empfängername mit dem Namen der Kontoinhaber*in übereinstimmt. Schon kleine Abweichungen können zu
                    Warnhinweisen oder Verzögerungen führen.
                  </p>
                  <cmpr-copy-button
                    class="mt-80"
                    :clipboard="donationAccountIban"
                    data-analytics-context="payment-step"
                    feedback-duration="2"
                    feedback-text="IBAN kopiert!"
                    size="x-small"
                    variant="outline"
                  >
                    IBAN kopieren
                  </cmpr-copy-button>
                </slot>
              </div>
            </template>

            <template #paypal>
              <div class="payment-step__notice">
                <cmpr-icon-solid-circle-info class="payment-step__notice-icon" />
                <slot name="paypal-advice">
                  Bitte schließe Deine Eingabe mit „{{ submitButtonLabel }}“ ab.
                  Du wirst umgehend auf eine PayPal-Seite geleitet.
                  Dort kannst Du Deine Spende mit Deinen PayPal-Anmeldedaten bestätigen.
                </slot>
              </div>
            </template>

            <template #klarna_paynow>
              <div class="payment-step__notice">
                <cmpr-icon-solid-circle-info class="payment-step__notice-icon" />
                Bitte schließe Deine Eingabe mit „{{ submitButtonLabel }}“ ab.
                Du wirst umgehend auf eine Klarna-Seite geleitet.
                Dort kannst Du Deine Spende mit Deinen Klarna-Anmeldedaten bestätigen.
              </div>
            </template>

            <template #paybybank>
              <div class="payment-step__notice">
                <cmpr-icon-solid-circle-info class="payment-step__notice-icon" />
                Bitte schließe Deine Eingabe mit „{{ submitButtonLabel }}“ ab.
                Du wirst umgehend auf eine Tink-Seite geleitet.
                Dort kannst Du Deine Spende mit Deinen Tink-Anmeldedaten bestätigen.
              </div>
            </template>

            <template #creditcard_adyen>
              <AdyenCreditcard
                :adyen-settings="adyenSettings"
              />
            </template>
          </PaymentTypeChoicesGroup>

          <cmpr-form-hint
            v-if="store.paymentErrorUnlocked && store.errors.paymentType"
            variant="error"
          >
            {{ store.errors.paymentType }}
          </cmpr-form-hint>
        </div>
      </template>

      <template #closed>
        <cmpr-prefilled-area
          v-if="showPaymentSummary"
          class="payment-step__summary"
          editable="editable"
          :variant="store.paymentStepValid ? 'success' : 'warning'"
        >
          <div class="fw-bold">
            {{ paymentSummaryTitle }}
          </div>

          <span
            v-if="paymentSummaryBody"
            class="summary__body"
          >
            {{ paymentSummaryBody }}
          </span>
        </cmpr-prefilled-area>
      </template>
    </StepItem>
  </div>
</template>

<style scoped lang="scss">
.payment-step__body {
  display: flex;
  flex-direction: column;
  gap: var(--cmpr-space-60);
}

.payment-step__notice {
  display: flex;
  flex-direction: row;
  font-size: var(--cmpr-font-size-20);
  gap: var(--cmpr-space-40);
  line-height: var(--cmpr-font-line-height-40);
}

.payment-step__notice-icon {
  color: var(--cmpr-color-information);
  font-size: var(--cmpr-font-size-30);
  margin-top: 3px;
}

.payment-step__banktransfer-info {
  font-size: var(--cmpr-font-size-30);
  line-height: var(--cmpr-font-line-height-40);
}

.payment-step__prefill-bankaccount {
  background-color: var(--cmpr-color-white);
  border: 1px solid var(--cmpr-color-neutral-40);
  margin-top: var(--cmpr-space-60);
  padding: var(--cmpr-space-60);

  .payment-step__prefill-bankaccount__input {
    border-color: var(--cmpr-color-secondary-80);
  }

  .payment-step__prefill-bankaccount__label {
    color: var(--cmpr-color-secondary-80);
    font-weight: bold;
  }

  .payment-step__prefill-bankaccount__description {
    font-size: var(--cmpr-font-size-20);
    grid-column: 2 / span 1;
  }
}
</style>
